import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { BsButton } from '@hotelplan/core.basis.bs-button';
import { EPageType } from '@hotelplan/fdr.lib.page.available-pages';
import { LOGIN_REDIRECT_PAGE_PARAM_NAME } from '@hotelplan/libs.login-redirect';
import { getRouteByPageTypeLocale } from '@hotelplan/libs.router-config';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import { useHolidayFinderWishlistItem } from './fdr-use-holiday-finder-wishlist-item';
import { LoginLink } from './fdr-wishlist-login-router-link';
import { ELoginClickSource, trackLoginClick } from './fdr-wishlist.tracking';

const WishlistNoAuthNotificationWrapper = styled.div(
  ({ theme: { media, space } }) => ({
    [media.tablet]: {
      flexBasis: '567px',
    },

    [media.mobile]: {
      paddingTop: space[2],
    },

    [media.desktop]: {
      marginBottom: space[2],
    },
  })
);

const WishlistNoAuthInfoWrapper = styled.div(
  ({ theme: { media, space, radii, FONT_SIZE } }) =>
    sx2CssThemeFn({
      paddingRight: space[4],
      paddingBottom: '20px',
      paddingLeft: space[4],
      borderRadius: radii.default,
      height: '100%',

      [media.mobile]: {
        paddingTop: '20px',
        paddingBottom: space[4],
      },

      '.wishlist-no-auth-content-title': {
        marginBottom: space[3],
        ...FONT_SIZE.XS,
      },
    })
);

const WishlistNoAuthHeader = styled.div(({ theme: { space } }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  '.wishlist-no-auth-content-button': {
    marginLeft: space[3],
    padding: 0,
    backgroundColor: 'transparent',
    color: '#000',
  },
}));

const WishlistNoAuthBenefits = styled.ul(
  ({ theme: { space, media, FONT_SIZE } }) =>
    sx2CssThemeFn({
      marginLeft: space[5],
      marginBottom: space[3],
      li: {
        listStyle: 'disc',
        lineHeight: 1.7,
        ...FONT_SIZE.LARGE,
      },
      [media.mobile]: {
        marginBottom: space[3],
      },
    })
);

const WishlistNoAuthButtons = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    flexDirection: ['column', 'row'],
    alignItems: ['initial', 'flex-end'],
  })
);

const LoginLinkWrapper = styled.div(
  sx2CssThemeFn({
    display: 'flex',
    flexDirection: ['column', 'row'],
    mr: [0, 3],
    width: ['100%', 'initial'],
  })
);

const LoginButton = styled(BsButton)(
  sx2CssThemeFn({
    mb: [3, 0],
    width: '100%',
  })
);

const FdrWishlistNoAuthNotification: React.FC = () => {
  const [t, { language }] = useTranslation('wishlist');
  const wishlistRoute = getRouteByPageTypeLocale(EPageType.Wishlist, language);
  const query = {
    [LOGIN_REDIRECT_PAGE_PARAM_NAME]: wishlistRoute.builder({}),
  };

  const { withHolidayFinder, loading } = useHolidayFinderWishlistItem();

  if (loading) {
    return null;
  }

  return (
    <WishlistNoAuthNotificationWrapper>
      <WishlistNoAuthInfoWrapper>
        <WishlistNoAuthHeader>
          <h3 className="wishlist-no-auth-content-title">
            {withHolidayFinder
              ? t('wishlist:noAuthNotification.contest.title')
              : t('wishlist:noAuthNotification.title')}
          </h3>
        </WishlistNoAuthHeader>
        {!withHolidayFinder ? (
          <WishlistNoAuthBenefits>
            <li>{t('wishlist:noAuthNotification.benefits.1')}</li>
            <li>{t('wishlist:noAuthNotification.benefits.2')}</li>
            <li>{t('wishlist:noAuthNotification.benefits.3')}</li>
            <li>{t('wishlist:noAuthNotification.benefits.4')}</li>
          </WishlistNoAuthBenefits>
        ) : null}
        <WishlistNoAuthButtons>
          <LoginLinkWrapper>
            <LoginLink
              language={language}
              query={new URLSearchParams(query).toString()}
            >
              <LoginButton
                onClick={() => {
                  trackLoginClick(ELoginClickSource.WISHLIST);
                }}
              >
                {t('wishlist:noAuthNotification.loginLink')}
              </LoginButton>
            </LoginLink>
          </LoginLinkWrapper>
        </WishlistNoAuthButtons>
      </WishlistNoAuthInfoWrapper>
    </WishlistNoAuthNotificationWrapper>
  );
};

export default FdrWishlistNoAuthNotification;
